import type { QuoteTemplateEntry } from '@common/types/quoteTemplates';
import { Box, Tab, Tabs, useTheme } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import CustomTabPanel from '@/components/CustomTabPanel';
import PageWrapper from '@/components/PageWrapper';
import { useLawFirmContext } from '@/hooks/useLawFirmContext';
import { useTemplates } from '@/queries/templates';
import { useUsers } from '@/queries/users';
import { deleteQuoteTemplate } from '@/services/QuoteTemplatesService';
import { errorSnackbar, successSnackbar, warningSnackbar } from '@/utils/snackbar';
import TemplatesList from './TemplatesList';
import type { FilterConfig } from './types';

function filterTemplates(
    templates: QuoteTemplateEntry[],
    config: FilterConfig,
): QuoteTemplateEntry[] {
    const { matterType, owner, searchKey } = config;
    return templates.filter((template) => {
        const isMatterTypeMatched = matterType === '' || template?.process === matterType;
        const isSearchKeyMatched =
            searchKey === '' ||
            template?.quoteTemplateName?.toLowerCase().includes(searchKey.toLocaleLowerCase());
        const isOwnerMatched = owner === 0 || template.quoteApproverId === owner;
        return isMatterTypeMatched && isSearchKeyMatched && isOwnerMatched;
    });
}

export default function Templates() {
    const theme = useTheme();
    const queryClient = useQueryClient();
    const [currentTab, setCurrentTab] = useState<number>(0);
    const { tab } = useParams();
    const navigate = useNavigate();

    const { activeLawFirmId } = useLawFirmContext();

    const [filterConfig, setFilterConfig] = useState<FilterConfig>({
        matterType: '',
        owner: 0,
        searchKey: '',
    });

    const { data: templates = [], isLoading: isLoadingTemplates } = useTemplates(activeLawFirmId);

    const { data: users = [] } = useUsers({ lawFirmId: activeLawFirmId });

    const deleteMutation = useMutation({
        mutationFn: deleteQuoteTemplate,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['templates'] });
            successSnackbar('Template deleted successfully');
        },
        onError: (error) => {
            console.error(error);
            if (isAxiosError(error) && error.status === 409) {
                warningSnackbar((error.response?.data as string) ?? 'Unable to delete template');
            } else {
                errorSnackbar('Failed to delete template');
            }
        },
    });

    const handleDeleteClick = (quoteTemplateId: number) => {
        deleteMutation.mutate(quoteTemplateId);
    };

    const quoteTemplates = useMemo(
        () => templates.filter((template) => template.templateType === 'QuoteIntake'),
        [templates],
    );

    const intakeTemplates = useMemo(
        () => templates.filter((template) => template.templateType === 'IntakeOnly'),
        [templates],
    );

    const filteredQuoteTemplates = useMemo(
        () => filterTemplates(quoteTemplates, filterConfig),
        [quoteTemplates, filterConfig],
    );

    const filteredIntakeTemplates = useMemo(
        () => filterTemplates(intakeTemplates, filterConfig),
        [intakeTemplates, filterConfig],
    );

    // Reset the owner filter when the effective law firm changes,
    // since user IDs from one firm won't be valid for another.
    useEffect(() => {
        setFilterConfig((prev) => (prev.owner !== 0 ? { ...prev, owner: 0 } : prev));
    }, [activeLawFirmId]); // oxlint-disable-line react-hooks/exhaustive-deps -- Need to reset filters when law firm ID changes

    useEffect(() => {
        if (!tab) return;
        if (tab === 'quotes') {
            setCurrentTab(0);
        } else if (tab === 'intakes') {
            setCurrentTab(1);
        } else {
            setCurrentTab(0);
        }
    }, [tab]);

    const handleTabChange = (tabValue: number) => {
        if (tabValue === 0) {
            navigate('/templates/quotes');
        } else if (tabValue === 1) {
            navigate('/templates/intakes');
        }
    };

    return (
        <PageWrapper title="Templates">
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: theme.palette.neutral.N_100,
                }}
            >
                <Tabs
                    value={currentTab}
                    variant="standard"
                    onChange={(_event, value) => handleTabChange(value)}
                >
                    <Tab sx={{ textTransform: 'none' }} label="Quote & Intake" />
                    <Tab sx={{ textTransform: 'none' }} label="Intake Only" />
                </Tabs>
            </Box>
            <CustomTabPanel value={currentTab} index={0}>
                <TemplatesList
                    templates={filteredQuoteTemplates}
                    allTemplates={quoteTemplates}
                    isLoading={isLoadingTemplates}
                    filterConfig={filterConfig}
                    setFilterConfig={setFilterConfig}
                    handleDeleteClick={handleDeleteClick}
                    templateType="QuoteIntake"
                    users={users}
                    isDeleteLoading={deleteMutation.isPending}
                />
            </CustomTabPanel>
            <CustomTabPanel value={currentTab} index={1}>
                <TemplatesList
                    templates={filteredIntakeTemplates}
                    allTemplates={intakeTemplates}
                    isLoading={isLoadingTemplates}
                    filterConfig={filterConfig}
                    setFilterConfig={setFilterConfig}
                    handleDeleteClick={handleDeleteClick}
                    templateType="IntakeOnly"
                    users={users}
                    isDeleteLoading={deleteMutation.isPending}
                />
            </CustomTabPanel>
        </PageWrapper>
    );
}
